import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import FloatingAvatar from './FloatingAvatar';
import { useClickCounter } from '../hooks/useClickCounter';

interface HeroSectionProps {
  onLogoEasterEgg?: () => void;
}

const HeroSection = ({ onLogoEasterEgg }: HeroSectionProps) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const { handleClick } = useClickCounter(10, () => {
    if (onLogoEasterEgg) onLogoEasterEgg();
  });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // System stats (public landing page - no sensitive data)
  const stats = [
    { label: 'Active Agents', value: '4', icon: '🤖' },
    { label: 'System Uptime', value: '99.8%', icon: '⚡' },
    { label: 'Total Scans', value: '15,000+', icon: '🔍' },
  ];

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-20">
      {/* Floating Avatar */}
      <FloatingAvatar mousePosition={mousePosition} onClick={handleClick} />

      {/* Main Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
        className="text-center mb-12 relative z-20"
      >
        <h1 className="text-6xl md:text-8xl lg:text-9xl font-black text-white mb-4 text-glow">
          MacMind
        </h1>
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-cyan-400" />
          <p className="text-xl md:text-3xl font-medium text-cyan-400 tracking-wider">
            THE NETWORK AWAKENS
          </p>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-cyan-400" />
        </div>
        <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
          AI-powered network monitoring • Autonomous agents • Real-time intelligence
        </p>
      </motion.div>

      {/* Stats Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl relative z-20"
      >
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
            className="glass glass-hover p-4 rounded-lg text-center"
          >
            <div className="text-3xl mb-2">{stat.icon}</div>
            <div className="text-2xl md:text-3xl font-bold text-white mb-1">
              {stat.value}
            </div>
            <div className="text-xs md:text-sm text-gray-400 font-medium">
              {stat.label}
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1, repeat: Infinity, repeatType: 'reverse' }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 text-gray-500">
          <span className="text-sm">Explore</span>
          <svg className="w-6 h-6 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </motion.div>
    </div>
  );
};

export default HeroSection;
